import _ from 'lodash';

import {getLanguage} from './language.utils';
import APP_CONFIG from '../app.config';
import {throwError} from './common.utils';
import ImmutableModelCache from './immutable-model.cache';
import ResourceCollection from './resource.collection';

/**
 * Request type for getting many items.
 * @type {symbol}
 */
const GET_MANY = Symbol('get_many');

/**
 * Request type for getting one item.
 * @type {symbol}
 */
const GET_ONE = Symbol('get_one');

/**
 * Header to use for language argument.
 * @type {string}
 */
const LANGUAGE_HEADER = 'Accept-Language';

const mod = angular.module('app.common.resource', [])

/**
 * An abstract resource to combine all common operations. Automatically adds the language argument to backend requests.
 */
.factory('AbstractResource', function ($http, $q) {
  /**
   * An api that is offered to external users. This api contains the common operations shared
   * by all resources.
   *
   * An AbstractResource contains methods for getting one or all of the objects from the backend and stores them as
   * ImmutableModel instances. When new objects are retrieved, they are merged with the old objects (which will be
   * overwritten since they are immutable). Matching is done with the property 'id' so each object should have that.
   */
  class AbstractResource {
    /**
     * @param {String} url Base URL of the resource, used as-is for getMany, ID is appended for getOne.
     * @param {function(object, object): ImmutableModel} mapper Mapping function that is executed for each object
     *   retrieved from the backend. In case of getMany, it is executed for every object in the result list. It MUST
     *   return an object that is an instance of a child class of ImmutableModel with at least a property 'id'. The
     *   mapper also receives the params sent with the request as the second argument.
     * @param {Boolean} [cache=false] Should the view be cached in ImmutableModelCache
     */
    constructor(url, mapper, cache=false) {
      /**
       * Base URL which all endpoints are under.
       * @type {string}
       */
      this.base_url = APP_CONFIG.be;


      this.url = `${APP_CONFIG.be}/${url}/`;
      this.mapper = mapper;

      this.lang = getLanguage();

      // Headers to send to backend with every request
      this._headers = {[LANGUAGE_HEADER]: this.lang};

      if (cache) {
        this.cache = new ImmutableModelCache($q, this);
      }
      else {
        this.cache = null;
      }

      // List of requests already in progress that can be used instead of a new request if arguments match
      this._inProgressRequests = [];
    }

    /**
     * GETs multiple data from server (for requests that return a list of values)
     *
     * @param {Object} filters Dictionary of query arguments to send.
     * @param {function(object, object): ImmutableModel} mapper Mapper overriding the default mapper.
     * @returns {Promise.<ResourceCollection>} Request promise that is resolved with the
     *   returned data.
     */

    getMany(filters={}, mapper=this.mapper) {
      const getter = params => {
        const in_progress_promise = this._checkInProgress(GET_MANY, params);

        if (in_progress_promise != null) {
          return in_progress_promise;
        }

        const promise = $http.get(AbstractResource.resolveUrl(this.url), {params, headers: this._headers})
        .then(response => {
          const objs = response.data.map(obj => mapper(obj, params));
          return new ResourceCollection(objs).makeImmutable();
        });

        this._inProgressRequests.push([GET_MANY, params, promise]);

        return promise
        .finally(() => {
          // Remove promise from list when request is done
          const idx = this._findInProgressIndex(GET_MANY, params);
          this._inProgressRequests.splice(idx, 1);
        });
      };

      if (this.cache) {
        return this.cache.getMany(filters, getter);
      }
      else {
        return getter(filters);
      }
    }

    /**
     * GETs one given object from the server
     *
     * @param {Number|String} id ID of object to retrieve from backend, number or string based on the API.
     * @param {function(object, object): ImmutableModel} mapper Mapper overriding the default mapper.
     * @param {object} query_args Query arguments to send.
     * @returns {Promise.<ImmutableModel>} Request promise that is resolved with the new object.
     */
    getOne(id, mapper=this.mapper, query_args={}) {
      const getter = id => {
        const in_progress_promise = this._checkInProgress(GET_ONE, {id});

        if (in_progress_promise != null) {
          return in_progress_promise;
        }

        const promise = $http.get(
          AbstractResource.resolveUrl(this.url, id),
          {headers: this._headers, params: query_args}
        )
        .then(response => mapper(response.data, {}));

        this._inProgressRequests.push([GET_ONE, {id}, promise]);

        return promise
        .finally(() => {
          const idx = this._findInProgressIndex(GET_ONE, {id});
          this._inProgressRequests.splice(idx, 1);
        });
      };

      if (this.cache) {
        return this.cache.getOne(id, getter);
      }
      else {
        return getter(id);
      }
    }

    /**
     * Shortcut for getting all data from backend (calling getMany without filters).
     * @param {function(object, object): ImmutableModel} mapper Mapper overriding the default mapper.
     * @return {Promise.<ResourceCollection>}
     */
    getAll(mapper=this.mapper) {
      return this.getMany({}, mapper);
    }

    /**
     * Get reuqest method and url and response status and data as a string to be logged with error logger.
     * @param {Response} response the response to get the error message for
     * @returns {String} the error message
     */
    getErrorStatusAndDataMessage(response) {
      try {
        if (response.status != null && response.status > 399) {
          const msg = response.config != null ? `${response.config.method} ${response.config.url} ${response.status}` : response.status;
          return response.data != null ? `${msg}: ${JSON.stringify(response.data)}` : msg;
        }
        return '';
      } catch (err) {
        return '';
      }
    }

    /**
     * Resolves the given URL from api.url and reservation id.
     *
     * @param {String} baseUrl URL to use as base for formulating the URL. E.g. /foo/.
     * @param {Number|String} id ID of the data to retrieve from backend, appended to baseUrl.
     * @param {Boolean} throwIfIdUndefined if true, method throws on undefined id parameter.
     * @returns {String} reservation URL to use for CRUD operations
     */
    static resolveUrl(baseUrl, id=null, throwIfIdUndefined=false) {
      if (throwIfIdUndefined && id == null) {
        throwError('Missing URL construction parameter in resolveUrl!');
      }

      let ret = baseUrl + (id != null? id : '');

      // Make sure our requests always end in / to avoid unnecessary redirects
      if (ret[ret.length - 1] !== '/') {
        ret += '/';
      }

      return ret;
    }

    // Find index of request with given type and params in list of in progress requests
    _findInProgressIndex(type, params) {
      return this._inProgressRequests.findIndex(([t, p]) => t === type && _.isEqual(p, params));
    }

    // Return the promise of an in progress request if one matching the given type and params exists, otherwise null
    _checkInProgress(type, params) {
      const idx = this._findInProgressIndex(type, params);

      if (idx === -1) {
        return null;
      }
      else {
        const [, , promise] = this._inProgressRequests[idx];
        return promise;
      }
    }
  }

  return AbstractResource;
})

;

export default mod.name;

export {
  LANGUAGE_HEADER
};
